import * as Dialog from '@radix-ui/react-dialog'
import { X, Keyboard } from 'lucide-react'
import { shortcuts } from '@/data/shortcuts'

interface KeyboardShortcutsModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

function KeyBadge({ label }: { label: string }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 bg-white/10 border border-white/20 rounded text-xs font-mono text-gray-300">
      {label}
    </kbd>
  )
}

export function KeyboardShortcutsModal({ open, onOpenChange }: KeyboardShortcutsModalProps) {
  const categories = Array.from(new Set(shortcuts.map((s) => s.category)))

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 bg-[#111] border border-white/10 rounded-xl p-6 shadow-2xl data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]"
          aria-describedby="shortcuts-description"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2.5">
              <Keyboard className="w-5 h-5 text-lime" />
              <Dialog.Title className="text-base font-semibold text-white">
                Keyboard Shortcuts
              </Dialog.Title>
            </div>
            <Dialog.Close
              aria-label="Close keyboard shortcuts"
              className="p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="w-4 h-4" />
            </Dialog.Close>
          </div>

          <p id="shortcuts-description" className="sr-only">
            List of available keyboard shortcuts grouped by category
          </p>

          <div className="space-y-5">
            {categories.map((category) => (
              <div key={category}>
                <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2.5">
                  {category}
                </h3>
                <div className="space-y-1">
                  {shortcuts
                    .filter((s) => s.category === category)
                    .map((shortcut) => (
                      <div
                        key={shortcut.description}
                        className="flex items-center justify-between py-1.5 px-2 rounded-lg hover:bg-white/5 transition-colors"
                      >
                        <span className="text-sm text-gray-300">{shortcut.description}</span>
                        <div className="flex items-center gap-1">
                          {shortcut.keys.map((key, idx) => (
                            <KeyBadge key={idx} label={key} />
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between">
            <span className="text-xs text-gray-400">Press</span>
            <div className="flex items-center gap-1.5">
              <KeyBadge label="?" />
              <span className="text-xs text-gray-400">to toggle this panel</span>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
